import React from 'react'
import * as clone from 'clone'
import { Container } from 'typescript-ioc/es5'
import { Button, Well, Drawer } from '@pushly/aqe/lib/components'
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { UserDto } from '../../dtos/user'
import { AccountService, AppService, UserService } from '../../services'
import { getClassNames } from '../../_utils/classnames'
import { simpleFormErrorNotification, simpleNotification } from '../../_utils/utils'
import { AppState } from '../../stores/app'
import randomstring from 'randomstring'
import { IUserAccountAccessView, UserAccountAccessView } from './user-access-view'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '../../constants'
import { AbilityAction } from '../../enums/ability-action.enum'
import { SubjectEntity } from '../../enums/ability-entity.enum'
import { asCaslSubject } from '../../stores/app-ability'
import { IOrgAccessRoleOption } from '@pushly/aqe/lib/components'
import { IOrgAccessSection } from './user-access-view'
import { OrgAccessRoleIds } from '@pushly/models/lib/enums/rbac'
import { InternalAccessRoleIds, ExternalApiAccessRoleIds, AccessRoleId } from '../../enums/access-role.enum'
import { noAccessUserRoles } from '../../services/access-control.service'
import { AccessRoleMap } from '../../models/access-role'
import AccountUserModel from '../../models/access-control/account-user.model'
import AccessControlService from '../../services/access-control.service'
import { DomainDto } from '../../dtos/domain'
import { observer } from 'mobx-react'
import { Flex, Form, Popover } from 'antd'

import './style/user-access-view.scss'

interface IUserAccessEditor extends IUserAccountAccessView {
    visible: boolean
    onClose: (e: any) => void
    onUpdated?: (account: Partial<UserDto>) => void
    orgAccess?: IOrgAccessSection
}

@observer
class UserAccessEditor extends React.Component<IUserAccessEditor, any> {
    protected appState: AppState
    protected appService: AppService
    protected userService: UserService

    protected accountSnapshot: any

    public constructor(props: IUserAccessEditor) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.userService = Container.get(UserService)

        this.accountSnapshot = clone(this.props.account)
    }

    public componentDidUpdate() {
        if (this.props.account && !this.accountSnapshot) {
            this.accountSnapshot = clone(this.props.account)
        }
    }

    public render() {
        return (
            <Drawer
                getContainer={this.appService.getAppContainer}
                className={getClassNames('user-access-editor')}
                classNames={{
                    body: getClassNames('user-access-editor-body'),
                }}
                title={
                    <>
                        <span>Edit User Access</span>
                        <Popover
                            classNames={{ content: getClassNames('popover', 'user-management-info-popover') }}
                            content={
                                <>
                                    To learn more about access levels visit our{' '}
                                    <a
                                        href={`${this.appState.documentationLink}/platform/user-management#access-levels`}
                                        target="_blank"
                                    >
                                        User Managment documentation
                                    </a>
                                    .
                                </>
                            }
                        >
                            <InfoCircleOutlined className="info-icon" />
                        </Popover>
                    </>
                }
                placement="right"
                closable={true}
                onSubmit={this.handleSubmit}
                disableSubmit={!this.props.acs.hasChanges}
                onClose={this.props.onClose}
                open={this.props.visible}
                bodyStyle={{ padding: 0 }}
                width={800}
            >
                {this.props.account && (
                    <Form {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}>
                        <UserAccountAccessView
                            orgAccess={this.props.orgAccess}
                            key={this.props.account?.id ?? 'NO_ACCT'}
                            mode="nested"
                            user={this.props.user}
                            acs={this.props.acs}
                            account={this.props.account}
                            domainId={this.props.domainId}
                            singleDomain={this.props.singleDomain ?? false}
                        />
                    </Form>
                )}
            </Drawer>
        )
    }

    protected get user(): UserDto {
        return this.props.user
    }

    protected handleSubmit = async () => {
        try {
            await this.props.acs.persistChanges()

            this.props.onUpdated?.({})

            this.props.onClose(null)
            simpleNotification('success', `User, ${this.props.user.name}, has been successfully updated.`)
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }
}

interface IUserAccessWell {
    user?: UserDto & any
    accountId?: number
    domainId?: number
    singleDomain?: boolean
    loading?: boolean
    onChange?: (permissions: any) => any
    onUpdated?: (account: Partial<UserDto>) => void
    mode?: 'default' | 'stand-alone' | 'nested' | 'read-only' | 'add-user'
    defaultValue?: any
}

interface IUserAccessWellState {
    account?: { id: number }
    domains: DomainDto[]
    loadingDependencies: boolean
    showDrawer: boolean
    drawerKey?: string
    propsUserACS: AccessControlService
}

@observer
export class UserAccessWell extends React.Component<IUserAccessWell, IUserAccessWellState> {
    protected appState: AppState
    protected appService: AppService
    protected accountService: AccountService
    protected userService: UserService

    public constructor(props: IUserAccessWell) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.accountService = Container.get(AccountService)
        this.userService = Container.get(UserService)

        this.state = {
            loadingDependencies: false,
            domains: [],
            showDrawer: false,
            // accountId scopes the abilities fetch to the org-scoped endpoint —
            // required whenever props.user is not the logged-in user.
            propsUserACS: AccessControlService.forUser(props.user, props.accountId),
        }
    }

    public async componentDidMount(): Promise<void> {
        this.fetchDependencies()
    }

    public render() {
        const { mode } = this.props
        const modeIsDefault = mode === 'default' || mode === 'nested' || mode === 'read-only'

        const wellType: any = {
            variant: modeIsDefault ? 'default' : 'ghost',
            padding: modeIsDefault ? 'base' : 'none',
        }
        return (
            <Flex vertical gap={8}>
                <Well
                    className={getClassNames('user-access-well', 'nested type-2', {
                        ['user-access-nested-well']: !modeIsDefault,
                    })}
                    title={
                        <>
                            <span>User Access</span>
                            <Popover
                                classNames={{
                                    content: 'user-management-info-popover',
                                }}
                                content={
                                    <>
                                        To learn more about access levels visit our{' '}
                                        <a
                                            href={`${this.appState.documentationLink}/platform/user-management#access-levels`}
                                            target="_blank"
                                        >
                                            User Management documentation
                                        </a>
                                        .
                                    </>
                                }
                            >
                                <InfoCircleOutlined className="info-icon" />
                            </Popover>
                        </>
                    }
                    styles={{ body: { padding: 0 } }}
                    footerMode="hidden"
                    {...wellType}
                    loading={this.isLoading}
                    actions={!this.isLoading && this.buildActions()}
                >
                    {!this.state.account ? (
                        <div className="no-account-selected-message">
                            Please select an organization to view permissions.
                        </div>
                    ) : (
                        <>
                            <UserAccountAccessView
                                key={this.state.account?.id ?? 'NO_ACCT'}
                                mode={mode}
                                disabled={true}
                                orgAccess={this.orgAccessSection(
                                    mode === 'add-user' && this.canEditOrganizationAccess,
                                )}
                                user={this.user}
                                acs={this.state.propsUserACS}
                                account={this.state.account}
                                domainId={this.props.domainId}
                                singleDomain={this.props.singleDomain ?? false}
                                onChange={(acs) => {
                                    this.props.onChange?.(acs.getDirtyRecords())
                                }}
                            />
                        </>
                    )}

                    <UserAccessEditor
                        key={this.state.drawerKey}
                        visible={this.state.showDrawer}
                        onClose={this.handleDrawerClose}
                        onUpdated={this.props.onUpdated}
                        acs={this.state.propsUserACS}
                        mode={mode}
                        user={this.user}
                        orgAccess={this.orgAccessSection(this.canEditOrganizationAccess)}
                        account={this.state.account!}
                        domainId={this.props.domainId}
                        singleDomain={this.props.singleDomain ?? false}
                        onChange={(acs) => {
                            this.props.onChange?.(acs.getDirtyRecords())
                        }}
                    />
                </Well>
            </Flex>
        )
    }

    /**
     * Organization access is only offered in an organization context. A domain
     * context is scoped to one domain, and a domain admin cannot write an
     * organization role -- the server refuses it -- so offering the control there
     * would be a promise the API does not keep.
     */
    protected orgAccessSection(editable: boolean) {
        if (this.props.domainId || !this.state.account) {
            return undefined
        }

        return {
            options: this.orgAccessRoleOptions,
            value:
                this.state.propsUserACS.getAccountRecord(this.state.account.id)?.getRoleId() ??
                this.state.propsUserACS.noAccessRoleId,
            readOnly: !editable,
            // Cascading roles are governed by the user record, not the account_users
            // row, so the row must not offer to overwrite them.
            disabled: this.user.isApiUser || this.user.isInternalDevUser,
            onChange: this.updateOrganizationRole,
        }
    }

    protected get orgAccessRoleOptions(): IOrgAccessRoleOption[] {
        // Mirrors how the domain axis picks its list. The platform manages external
        // users, but it displays internal and API ones, and a stored role missing
        // from this list renders the row blank and offers a swap the server refuses.
        const apiRoleIds = [AccessRoleId.INTERNAL_API_USER, ...ExternalApiAccessRoleIds]
        let roleIds = this.user.isInternalUser ? InternalAccessRoleIds : OrgAccessRoleIds

        if (this.user.id && !this.user.isApiUser) {
            roleIds = roleIds.filter((id) => !apiRoleIds.includes(id))
        }

        return roleIds.map((roleId) => ({
            value: roleId,
            label: AccessRoleMap.get(roleId)?.label ?? '',
        }))
    }

    /**
     * The org subject must carry `statusId: 1`. The external account policy is
     * conditioned on `{ id, statusId: 1 }` while the internal one is only
     * conditioned on `{ id }`, and CASL matches a rule's conditions against the
     * subject's fields -- so omitting statusId means the external rule never
     * matches and org admins silently lose the control. Active status is
     * guaranteed here because the public platform only surfaces active orgs.
     */
    protected get canEditOrganizationAccess(): boolean {
        return this.appState.abilityStore.can(
            AbilityAction.UPDATE,
            asCaslSubject(SubjectEntity.ORG, { id: this.props.accountId, statusId: 1 }),
        )
    }

    /**
     * Mutates the same cached organization record the domain table reuses. That
     * record is the parent of every domain row, so it is cloned and updated
     * rather than replaced, and the domain argument is left off so a role change
     * here never marks a domain record dirty.
     */
    protected updateOrganizationRole = async (roleId: number) => {
        const accessRole = AccessRoleMap.get(roleId)
        const accountId = this.state.account?.id
        if (!accessRole || !accountId) {
            return
        }

        let orgRecord = this.state.propsUserACS.getAccountRecord(accountId)?.clone()

        if (orgRecord) {
            orgRecord.setRoleId(accessRole.id)
            orgRecord.setRoleName(accessRole.name)
        } else {
            orgRecord = new AccountUserModel({
                accountId,
                userId: this.user.id,
                roleId: accessRole.id,
                roleName: accessRole.name,
                flags: [],
            })
        }

        // Creating a row that grants nothing is not a change. Without this, picking
        // the No Access already on display stages a create and leaves the form
        // permanently dirty -- the domain path guards the same case.
        const isNewNoAccessRecord =
            !this.state.propsUserACS.getAccountRecord(accountId, true) &&
            noAccessUserRoles.includes(accessRole.id)

        if (isNewNoAccessRecord) {
            return
        }

        await this.state.propsUserACS.updateCachedRecord(accountId, orgRecord)
        this.props.onChange?.(this.state.propsUserACS.getDirtyRecords())
    }

    protected get isLoading(): boolean {
        return this.props.loading || this.state.loadingDependencies
    }

    protected get user(): UserDto {
        return this.props.user || new UserDto()
    }

    protected buildActions() {
        const { mode } = this.props
        const isNestedMode = mode === 'nested' || mode === 'add-user'

        // Not gated on organization rights: this drawer edits domain access as well,
        // and a domain admin has no organization ability. The organization control
        // inside it carries its own gate.
        return isNestedMode ? (
            ''
        ) : (
            <span key="edit">
                <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                    <EditOutlined />
                    <span>Edit</span>
                </Button>
            </span>
        )
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        await this.state.propsUserACS.revertUnsubmittedRecords()
        return this.setState({ showDrawer: false })
    }

    protected async fetchDependencies(): Promise<void> {
        this.setState({ loadingDependencies: true })

        if (this.props.accountId) {
            this.setState({ account: { id: this.props.accountId } })
        }

        try {
            if (this.props.user) {
                await this.state.propsUserACS.getUserRecords()
            }
        } catch (err) {
            // Without this the well spins forever on any request failure:
            // componentDidMount does not await or catch fetchDependencies, and
            // getAbilities rethrows on a non-2xx, so loadingDependencies would
            // never be cleared and the rejection would go unhandled. That is the
            // hang reported in SD-4850, where the underlying cause was a 403.
            if (process.env.IS_LOCAL) {
                console.debug('user_access_well_dependencies_error', err)
            }
        } finally {
            this.setState({ loadingDependencies: false })
        }
    }

}
